import { createContext, useContext, useCallback, useState } from "react";
import {
  TopHeadValidateUrl,
  AddImageUrl,
  HairSegmentationImageUrl,
} from "../constants";

const ScanImageContext = createContext();

// Export the hook to access the UID context
export const useScanImage = () => {
  return useContext(ScanImageContext);
};

// Provider component to wrap around the app
export const ScanImageProvider = ({ children }) => {
  const [images, setImages] = useState({});
  const [frontAnimationImage, setFrontAnimationImage] = useState({
    isHairPresent: false,
    imageUrl: "",
    rgbaUrl: "",
    maskUrl: "",
    width: 2000,
    height: 3000,
  });

  const resetScanImage = () => {
    setImages({});
    setFrontAnimationImage({
      isHairPresent: false,
      imageUrl: "",
      rgbaUrl: "",
      maskUrl: "",
      width: 2000,
      height: 3000,
    });
  };

  const addFrontImage = (
    imageData,
    meshKeypoints = null,
    imageWidth = null,
    imageHeight = null,
  ) => {
    setImages((prevData) => ({ ...prevData, front: imageData }));
    setFrontAnimationImage((prevData) => ({
      ...prevData,
      isHairPresent: false,
      imageUrl: imageData,
      rgbaUrl: "",
      maskUrl: "",
      keypoints: meshKeypoints,
      width: imageWidth,
      height: imageHeight,
    }));
  };

  const addSideImage = (faceSide, imageData) => {
    setImages((prevData) => ({ ...prevData, [faceSide]: imageData }));
  };

  const uploadImages = async (apiToken) => {
    try {
      for (const [faceSide, imageData] of Object.entries(images)) {
        const imageContent = imageData
          .toString()
          .replace(/^data:image\/jpeg;base64,/, "");
        const response = await fetch(AddImageUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: apiToken,
          },
          body: JSON.stringify({
            image: imageContent,
            faceSide: faceSide,
          }),
        });
        if (!response.ok) {
          throw new Error("Failed to upload image");
        }
      }
      setImages({});
      return true;
    } catch (error) {
      return false;
    }
  };

  const getHairRegion = async (apiToken) => {
    try {
      const imageContent = frontAnimationImage.imageUrl
        .toString()
        .replace(/^data:image\/jpeg;base64,/, "");
      const response = await fetch(HairSegmentationImageUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: apiToken,
        },
        body: JSON.stringify({
          base64Image: imageContent,
        }),
      });
      if (!response.ok) {
        throw new Error("Failed to upload image");
      }

      const data = await response.json();
      const isHairDetected = data.isDetected;

      if (isHairDetected) {
        setFrontAnimationImage((prev) => ({
          ...prev,
          isHairPresent: true,
          rgbaUrl: data.imageHairMask,
          maskUrl: data.binaryHairMask,
        }));
      }
      return true;
    } catch (error) {
      return false;
    }
  };

  const validateTopHeadImage = useCallback(async () => {
    // In case topHead is not detected it should raise an error
    if (images.top === undefined) return { ok: false, isHeadDetected: false };

    try {
      const imageContent = images.top
        .toString()
        .replace(/^data:image\/jpeg;base64,/, "");
      const response = await fetch(TopHeadValidateUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams({
          imageBase64: imageContent,
        }).toString(),
      });
      if (!response.ok) {
        throw new Error("Failed to validate top head image");
      }
      const responseData = await response.json();
      return { ok: true, isHeadDetected: responseData?.headDetected ?? false };
    } catch (error) {
      return { ok: false, isHeadDetected: false };
    }
  }, [images]);

  const imagesCaptured = Object.keys(images).length > 0;

  return (
    <ScanImageContext.Provider
      value={{
        frontAnimationImage,
        imagesCaptured,
        resetScanImage,
        addFrontImage,
        getHairRegion,
        addSideImage,
        uploadImages,
        validateTopHeadImage,
      }}
    >
      {children}
    </ScanImageContext.Provider>
  );
};
